import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  inject,
  InjectionToken,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface CurrencyMaskConfig {
  align: string;
  allowNegative: boolean;
  allowZero: boolean;
  decimal: string;
  precision: number;
  prefix: string;
  suffix: string;
  thousands: string;
  nullable: boolean;
}

export const CURRENCY_MASK_CONFIG = new InjectionToken<CurrencyMaskConfig>('CurrencyMaskConfig');

function formatCurrency(digits: string, config: CurrencyMaskConfig): string {
  const precision = config.precision;
  const padded = digits.padStart(precision + 1, '0');
  const intPart = padded.slice(0, padded.length - precision);
  const decPart = padded.slice(padded.length - precision);
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, config.thousands);
  return config.prefix + intFormatted + (precision > 0 ? config.decimal + decPart : '') + config.suffix;
}

@Directive({
  selector: 'input[appCurrencyMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyMaskDirective),
      multi: true,
    },
  ],
})
export class CurrencyMaskDirective implements ControlValueAccessor, OnInit {
  private element = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private globalConfig = inject<CurrencyMaskConfig | null>(CURRENCY_MASK_CONFIG, { optional: true });

  @Input() public options: Partial<CurrencyMaskConfig> = {};

  private config: CurrencyMaskConfig = {
    align: 'left',
    allowNegative: false,
    allowZero: true,
    decimal: '.',
    precision: 2,
    prefix: '$',
    suffix: '',
    thousands: ',',
    nullable: true,
  };

  private lastKeyDownKey: string = '';
  private rawDigits = '';
  private onChange: (value: number | null) => void = () => {};
  public onTouched: () => void = () => {};

  public ngOnInit(): void {
    if (this.globalConfig) {
      this.config = { ...this.config, ...this.globalConfig };
    }
    this.config = { ...this.config, ...this.options };
    this.element.nativeElement.style.textAlign = this.config.align;
  }

  public writeValue(value: number | null): void {
    const el = this.element.nativeElement;
    if (value == null || value === undefined) {
      if (this.config.nullable) {
        this.rawDigits = '';
        el.value = '';
      } else {
        this.rawDigits = '0'.repeat(this.config.precision > 0 ? this.config.precision : 1);
        el.value = formatCurrency(this.rawDigits, this.config);
      }
    } else {
      const multiplied = Math.round(Math.abs(value) * Math.pow(10, this.config.precision));
      this.rawDigits = multiplied.toString();
      el.value = formatCurrency(this.rawDigits, this.config);
    }
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(disabled: boolean): void {
    this.element.nativeElement.disabled = disabled;
  }

  private isReadonly(): boolean {
    return this.element.nativeElement.hasAttribute('readonly');
  }

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    this.lastKeyDownKey = event.key;
  }

  @HostListener('input', ['$event'])
  public onInput(_event: Event): void {
    if (this.isReadonly()) return;
    const el = this.element.nativeElement;

    const initialCursor = el.selectionStart ?? 0;
    const textAfterCursor = el.value.slice(initialCursor);
    let digitsAfterCursor = textAfterCursor.replace(/\D/g, '').length;

    let digits = el.value.replace(/\D/g, '');

    if (digits === this.rawDigits && this.rawDigits !== '') {
      if (digits.length > 0) {
        let deleteTargetIndex: number;

        if (this.lastKeyDownKey === 'Delete') {
          deleteTargetIndex = digits.length - digitsAfterCursor;

          if (deleteTargetIndex < digits.length) {
            digits = digits.slice(0, deleteTargetIndex) + digits.slice(deleteTargetIndex + 1);
          }
        } else if (this.lastKeyDownKey === 'Backspace') {
          deleteTargetIndex = Math.max(0, digits.length - digitsAfterCursor - 1);
          digits = digits.slice(0, deleteTargetIndex) + digits.slice(deleteTargetIndex + 1);

          if (digitsAfterCursor > 0 && deleteTargetIndex >= digits.length - digitsAfterCursor) {
            digitsAfterCursor = Math.max(0, digitsAfterCursor - 1);
          }
        }
      }
    }

    // Reset keyboard tracking state flag
    this.lastKeyDownKey = '';

    // relax zero scrubbing to allow 0.00 when user enters 0
    if (digits.length > 1) {
      digits = digits.replace(/^0+/, '');
    }

    if (digits === '') {
      if (this.config.nullable) {
        this.rawDigits = '';
        el.value = '';
        this.onChange(null);
        return;
      } else {
        digits = '0';
      }
    }

    this.rawDigits = digits;

    el.value = formatCurrency(this.rawDigits, this.config);
    const numericValue = parseInt(this.rawDigits, 10) / Math.pow(10, this.config.precision);
    this.onChange(numericValue);

    setTimeout(() => {
      let targetCursor = el.value.length - this.config.suffix.length;
      let digitCountFromRight = 0;

      for (let i = el.value.length - this.config.suffix.length - 1; i >= this.config.prefix.length; i--) {
        if (digitCountFromRight === digitsAfterCursor) {
          targetCursor = i + 1;
          break;
        }
        if (/\d/.test(el.value[i])) {
          digitCountFromRight++;
        }
        if (digitCountFromRight === digitsAfterCursor) {
          targetCursor = i;
          break;
        }
      }

      // Safe bounds validation
      const maxPos = el.value.length - this.config.suffix.length;
      if (targetCursor > maxPos) targetCursor = maxPos;
      if (targetCursor < this.config.prefix.length) targetCursor = this.config.prefix.length;

      el.selectionStart = el.selectionEnd = targetCursor;
    });
  }

  @HostListener('paste', ['$event'])
  public onPaste(_event: ClipboardEvent): void {
    // Handled natively by the unified onInput listener immediately after execution
  }

  @HostListener('drop', ['$event'])
  public onDrop(event: DragEvent): void {
    if (this.isReadonly()) event.preventDefault();
  }

  @HostListener('blur')
  public onBlur(): void {
    this.onTouched();
  }
}
